<template>
  <div v-if="companies.length" class="member-companies mt-5">
    <h2 class="member-title h3">Empresas</h2>
    <p class="text-muted">Administras la empresa. El cupo y los beneficios son de cada empleado.</p>
    <b-alert v-if="errorMessage" show variant="danger" dismissible @dismissed="errorMessage = null">
      {{ errorMessage }}
    </b-alert>
    <b-alert v-if="pendingInvoiceUrl" show variant="warning">
      Hay un cobro pendiente.
      <b-button class="ml-2" size="sm" variant="warning" :href="pendingInvoiceUrl" target="_blank" rel="noopener">
        Pagar factura
      </b-button>
    </b-alert>

    <div class="mb-3">
      <label class="small mb-1" for="company-select">Empresa</label>
      <b-form-select id="company-select" v-model="selectedId" :options="companyOptions" @change="loadDetail" />
    </div>

    <b-spinner v-if="loading" />
    <div v-else-if="detail">
      <p class="mb-1"><strong>{{ companyName }}</strong></p>
      <p class="mb-1">Cupos: {{ seats.used || 0 }} usados de {{ seats.total || 0 }}. Libres: {{ seats.free || 0 }}. Mínimo: {{ seats.minimum || 5 }}.</p>
      <p v-if="subscription" class="mb-3">
        Cobro {{ intervalLabel }}: ${{ subscription.amount }}
        <span v-if="subscription.cancel_at"> · se cancela el {{ subscription.cancel_at }}</span>
      </p>

      <div class="d-flex flex-wrap mb-3">
        <b-button v-if="canPay" class="mr-2 mb-2" variant="primary" :disabled="acting" @click="pay">Pagar</b-button>
        <b-button v-if="canCancel" class="mr-2 mb-2" variant="outline-danger" :disabled="acting" @click="cancel">Cancelar</b-button>
        <b-button v-if="canResume" class="mr-2 mb-2" variant="outline-primary" :disabled="acting" @click="resume">Reanudar</b-button>
      </div>

      <b-row class="mb-3">
        <b-col cols="12" md="4">
          <label class="small mb-1" for="seat-quantity">Reducir cupos</label>
          <b-form-input id="seat-quantity" v-model.number="seatQuantity" type="number" :min="seatFloor" :max="seats.total || seatFloor" />
          <small class="text-muted">Solo baja cupos, desde la siguiente renovación. Piso {{ seatFloor }}.</small>
        </b-col>
        <b-col cols="12" md="4" class="d-flex align-items-end">
          <b-button variant="outline-secondary" class="mb-4" :disabled="acting" @click="reduceSeats">Aplicar</b-button>
        </b-col>
      </b-row>

      <member-guarantee-card v-if="subscription && subscription.id" :subscription-id="subscription.id" class="mb-3" />

      <h3 class="h5">Empleados</h3>
      <b-table :items="employees" :fields="employeeFields" small responsive show-empty empty-text="Sin empleados.">
        <template #cell(name)="data">
          {{ data.item.name }} {{ data.item.lastname }}
        </template>
        <template #cell(pay_status)="data">
          {{ data.item.removed_at ? 'Dado de baja' : (data.item.pending_payment ? 'Pago pendiente' : payStatusLabel(data.item.pay_status)) }}
        </template>
        <template #cell(actions)="data">
          <b-button
            v-if="!data.item.removed_at"
            size="sm"
            variant="outline-danger"
            :disabled="acting"
            @click="removeEmployee(data.item)"
          >
            Dar de baja
          </b-button>
        </template>
      </b-table>

      <h3 class="h5 mt-3">Alta de empleado</h3>
      <b-row>
        <b-col cols="12" md="4" class="mb-2">
          <b-form-input v-model.trim="employee.name" placeholder="Nombre" />
        </b-col>
        <b-col cols="12" md="4" class="mb-2">
          <b-form-input v-model.trim="employee.lastname" placeholder="Apellido" />
        </b-col>
        <b-col cols="12" md="4" class="mb-2">
          <b-form-input v-model="employee.date_birth" placeholder="MM-DD-YYYY" />
        </b-col>
        <b-col cols="12" md="4" class="mb-2">
          <b-form-input v-model.trim="employee.phone" placeholder="Teléfono (opcional)" />
        </b-col>
        <b-col cols="12" md="4" class="mb-2">
          <b-form-input v-model.trim="employee.email" placeholder="Correo (opcional)" />
        </b-col>
      </b-row>
      <b-button variant="primary" :disabled="acting" @click="addEmployee">Dar de alta</b-button>
    </div>
  </div>
</template>

<script>
import { BAlert, BButton, BCol, BFormInput, BFormSelect, BRow, BSpinner, BTable } from 'bootstrap-vue'
import memberService from '@/core/services/api/member'
import { membershipApiMessage } from '@/core/utils/membershipAdmin'
import { intervalCopy } from '@/core/utils/membershipBilling'
import { isValidDateBirthMdy } from '@/view/pages/landings/utils/registerUserPlanHelpers'
import { companyOf, PAY_STATUS_COPY, unwrapList, unwrapRecord } from '@/core/utils/membershipFamily'
import MemberGuaranteeCard from './MemberGuaranteeCard.vue'

function isValidDate(value) {
  return isValidDateBirthMdy(value)
}

/**
 * Perfil del administrador de empresa: cupos, altas, bajas, cancelar y pagar.
 */
export default {
  name: 'MemberCompanyPanel',
  components: { BAlert, BButton, BCol, BFormInput, BFormSelect, BRow, BSpinner, BTable, MemberGuaranteeCard },
  data() {
    return {
      companies: [],
      selectedId: null,
      detail: null,
      loading: false,
      acting: false,
      errorMessage: null,
      pendingInvoiceUrl: null,
      seatQuantity: 5,
      employee: { name: '', lastname: '', date_birth: '', phone: '', email: '' },
      employeeFields: [
        { key: 'name', label: 'Empleado' },
        { key: 'email', label: 'Correo' },
        { key: 'pay_status', label: 'Pago' },
        { key: 'actions', label: '' },
      ],
    }
  },
  computed: {
    companyOptions() {
      return this.companies.map((item) => {
        const company = companyOf(item)
        return { value: company.id, text: company.name || `Empresa ${company.id}` }
      })
    },
    companyName() {
      const company = this.detail && this.detail.company
      return (company && company.name) || 'Empresa'
    },
    seats() {
      return (this.detail && this.detail.seats) || {}
    },
    subscription() {
      return (this.detail && this.detail.subscription) || null
    },
    employees() {
      return (this.detail && this.detail.employees) || []
    },
    seatFloor() {
      const minimum = Number(this.seats.minimum)
      return Number.isFinite(minimum) ? minimum : 5
    },
    intervalLabel() {
      return intervalCopy(this.subscription && this.subscription.billing_interval).name
    },
    canPay() {
      // El checkout de empresa solo acepta pay_status 0; 3 y 4 responden 409.
      return this.subscription && Number(this.subscription.pay_status) === 0
    },
    canCancel() {
      return this.subscription && Number(this.subscription.pay_status) === 1 && !this.subscription.cancel_at
    },
    canResume() {
      return this.subscription && !!this.subscription.cancel_at
    },
  },
  mounted() {
    this.loadCompanies()
  },
  methods: {
    async loadCompanies() {
      try {
        const response = await memberService.getCompanies()
        this.companies = unwrapList(response)
        this.$emit('loaded', this.companies.length)
        if (!this.selectedId && this.companies.length) {
          this.selectedId = companyOf(this.companies[0]).id
          await this.loadDetail()
        }
      } catch (error) {
        const status = error && error.response && error.response.status
        if (status !== 403 && status !== 404 && status !== 500) {
          this.errorMessage = membershipApiMessage(error, 'No se pudieron cargar las empresas.')
        }
      }
    },
    async loadDetail() {
      if (!this.selectedId) return
      this.loading = true
      this.errorMessage = null
      try {
        const response = await memberService.getCompany(this.selectedId)
        this.detail = unwrapRecord(response)
        const pending = this.subscription && this.subscription.pending_invoice_url
        this.pendingInvoiceUrl = pending || null
        const total = Number(this.seats.total)
        this.seatQuantity = Number.isFinite(total) ? Math.max(this.seatFloor, total - 1) : this.seatFloor
      } catch (error) {
        this.detail = null
        this.errorMessage = membershipApiMessage(error, 'No se pudo cargar la empresa.')
      } finally {
        this.loading = false
      }
    },
    openInvoiceFromError(error) {
      const status = error && error.response && error.response.status
      const data = error && error.response && error.response.data
      if (status === 409 && data && data.type === 'pending_invoice') {
        // El 409 no trae el enlace: sale de subscription.pending_invoice_url del detalle.
        const message = data.message || 'La empresa tiene un pago pendiente.'
        this.loadDetail().then(() => {
          if (!this.pendingInvoiceUrl) this.errorMessage = message
        })
        return true
      }
      return false
    },
    payStatusLabel(value) {
      const copy = PAY_STATUS_COPY[Number(value)]
      return copy || (value != null ? String(value) : '—')
    },
    employeePayload() {
      const payload = {
        name: this.employee.name,
        lastname: this.employee.lastname,
        date_birth: this.employee.date_birth,
      }
      if (this.employee.phone) payload.phone = this.employee.phone
      if (this.employee.email) payload.email = this.employee.email
      return payload
    },
    async addEmployee() {
      if (!this.employee.name || !this.employee.lastname || !isValidDate(this.employee.date_birth)) {
        this.errorMessage = 'Nombre, apellido y fecha de nacimiento (MM-DD-YYYY) son obligatorios.'
        return
      }
      this.acting = true
      this.errorMessage = null
      try {
        const response = await memberService.addCompanyEmployee(this.selectedId, this.employeePayload())
        const record = unwrapRecord(response) || {}
        if (record.state === 'pending_payment' && record.pending_invoice_url) {
          this.pendingInvoiceUrl = record.pending_invoice_url
        }
        this.employee = { name: '', lastname: '', date_birth: '', phone: '', email: '' }
        await this.loadDetail()
      } catch (error) {
        this.errorMessage = membershipApiMessage(error, 'No se pudo dar de alta al empleado.')
      } finally {
        this.acting = false
      }
    },
    async removeEmployee(item) {
      this.acting = true
      this.errorMessage = null
      try {
        await memberService.removeCompanyEmployee(this.selectedId, item.membership_id)
        await this.loadDetail()
      } catch (error) {
        this.errorMessage = membershipApiMessage(error, 'No se pudo dar de baja al empleado.')
      } finally {
        this.acting = false
      }
    },
    async reduceSeats() {
      const quantity = Number(this.seatQuantity)
      const total = Number(this.seats.total)
      if (!Number.isInteger(quantity) || quantity < this.seatFloor || (Number.isFinite(total) && quantity >= total)) {
        this.errorMessage = `Los cupos solo pueden bajar, con un piso de ${this.seatFloor}.`
        return
      }
      this.acting = true
      this.errorMessage = null
      try {
        await memberService.updateCompanySeats(this.selectedId, quantity)
        await this.loadDetail()
      } catch (error) {
        this.errorMessage = membershipApiMessage(error, 'No se pudieron reducir los cupos.')
      } finally {
        this.acting = false
      }
    },
    async pay() {
      this.acting = true
      this.errorMessage = null
      try {
        const response = await memberService.checkoutCompany(this.selectedId)
        const record = unwrapRecord(response) || response || {}
        const url = record.checkout_url || record.checkoutUrl
        if (url) {
          window.location.href = url
          return
        }
        this.errorMessage = 'No se pudo abrir el pago.'
      } catch (error) {
        if (!this.openInvoiceFromError(error)) {
          this.errorMessage = membershipApiMessage(error, 'No se pudo iniciar el pago.')
        }
      } finally {
        this.acting = false
      }
    },
    async cancel() {
      this.acting = true
      try {
        await memberService.cancelCompany(this.selectedId)
        await this.loadDetail()
      } catch (error) {
        this.errorMessage = membershipApiMessage(error, 'No se pudo cancelar la empresa.')
      } finally {
        this.acting = false
      }
    },
    async resume() {
      this.acting = true
      try {
        await memberService.resumeCompany(this.selectedId)
        await this.loadDetail()
      } catch (error) {
        this.errorMessage = membershipApiMessage(error, 'No se pudo reanudar la empresa.')
      } finally {
        this.acting = false
      }
    },
  },
}
</script>
